// Real sign-in — ports the flow in public/index.html (google.accounts.id
// .initialize / renderButton / credential callback) rather than inventing a
// new one. Rendered only while signed out: no nav, no clinic data (SPEC-001
// §13 / this phase's brief, point 3).
import type { JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { googleClientId } from '../lib/config';
import { handleSignIn } from '../lib/session';
import { FaultState } from './FaultState';
import { assistantSignIn, assistantCodeSignIn } from '../lib/assistantApi';

interface GoogleIdShim {
  accounts?: {
    id?: {
      initialize?: (config: {
        client_id: string;
        callback: (response: { credential: string }) => void;
        hosted_domain?: string;
      }) => void;
      renderButton?: (parent: HTMLElement, options: { theme: string; size: string }) => void;
    };
  };
}

function google(): GoogleIdShim | undefined {
  return (window as unknown as { google?: GoogleIdShim }).google;
}

const GSI_WAIT_MS = 8_000;
const GSI_POLL_MS = 100;

export function SignIn(): JSX.Element {
  const clientId = googleClientId();
  const buttonHost = useRef<HTMLDivElement>(null);
  const [gsiError, setGsiError] = useState<string | null>(null);
  const [assistantBusy, setAssistantBusy] = useState(false);
  const [assistantError, setAssistantError] = useState<string | null>(null);

  const [codeOpen, setCodeOpen] = useState(false);
  const [username, setUsername] = useState('');
  const [code, setCode] = useState('');

  async function signInWithCode(): Promise<void> {
    setAssistantError(null);
    setAssistantBusy(true);
    try {
      const r = await assistantCodeSignIn(username.trim(), code.trim());
      await handleSignIn(r.sessionToken);
    } catch (err) {
      setAssistantError(err instanceof Error ? err.message : String(err));
    } finally {
      setAssistantBusy(false);
    }
  }

  async function signInAsAssistant(): Promise<void> {
    setAssistantError(null);
    setAssistantBusy(true);
    try {
      const r = await assistantSignIn();
      await handleSignIn(r.sessionToken);
    } catch (err) {
      setAssistantError(err instanceof Error ? err.message : String(err));
    } finally {
      setAssistantBusy(false);
    }
  }

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    let timer = 0;
    const started = Date.now();
    const tryRender = () => {
      if (cancelled) return;
      const id = google()?.accounts?.id;
      if (id?.initialize && id.renderButton && buttonHost.current) {
        try {
          id.initialize({
            client_id: clientId,
            callback: (response) => { void handleSignIn(response.credential); },
            hosted_domain: 'nvmanager.com',
          });
          id.renderButton(buttonHost.current, { theme: 'outline', size: 'large' });
          setGsiError(null);
        } catch (err) {
          setGsiError(err instanceof Error ? err.message : String(err));
        }
        return;
      }
      if (Date.now() - started >= GSI_WAIT_MS) {
        setGsiError('Google sign-in did not load. Refresh the page.');
        return;
      }
      timer = window.setTimeout(tryRender, GSI_POLL_MS);
    };
    tryRender();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [clientId]);

  if (!clientId) {
    return (
      <FaultState
        name="not configured"
        detail="Sign-in is not configured yet: no Google client ID has been set for this site."
      />
    );
  }

  return (
    <div class="sign-in-gate">
      <div class="sign-in-gate__prompt">Sign in with your nvmanager.com account.</div>
      <div ref={buttonHost} data-testid="google-signin-button" />
      <div class="sign-in-gate__assistant">
        <button type="button" data-testid="assistant-signin" disabled={assistantBusy} onClick={() => void signInAsAssistant()}>
          Sign in with your phone (Face ID)
        </button>
        <button type="button" data-testid="assistant-code-toggle" onClick={() => setCodeOpen(!codeOpen)}>
          Sign in with a code
        </button>
        {codeOpen ? (
          <form
            data-testid="assistant-code-form"
            onSubmit={(e: Event) => { e.preventDefault(); void signInWithCode(); }}
          >
            <input
              type="text" aria-label="Username" placeholder="Username (like pat.mevl)" autocomplete="username"
              autocapitalize="none" value={username}
              onInput={(e: Event) => setUsername((e.target as HTMLInputElement).value)}
            />
            <input
              type="text" inputMode="numeric" aria-label="6-digit code" placeholder="6-digit code" maxLength={7}
              autocomplete="one-time-code" value={code}
              onInput={(e: Event) => setCode((e.target as HTMLInputElement).value)}
            />
            <button type="submit" data-testid="assistant-code-submit" disabled={assistantBusy}>Sign in</button>
          </form>
        ) : null}
        {assistantError ? <div role="alert" data-testid="assistant-signin-error">{assistantError}</div> : null}
      </div>
      {gsiError ? <FaultState name="sign-in button missing" detail={gsiError} /> : null}
    </div>
  );
}
